import { useBatches } from '@api/hooks/useBatch.ts';
import { EmptyState, NoFilteredResults } from '@components/EmptyState';
import { PageContainer } from '@components/PageContainer';
import { useBankProfile } from '@contexts/BankProfileContext.tsx';
import {
	Button,
	DEFAULT_PAGE_SIZE,
	DropdownPositions,
	Loader,
	OperationIcon,
	Pagination,
	TransferIcon,
} from '@gurupay/gp-ui-library';
import { useClearFilter } from '@hooks/useClearFilter.ts';
import { usePagination } from '@hooks/usePagination.ts';
import {
	BATCH_OPERATION_TYPE,
	getDefaultFilters,
} from '@pages/batch-operations/utils.ts';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import type { BatchStatus } from 'types/operations.ts';
import { Capability } from 'types/roles.ts';

import { BatchListFilter } from './batch/components/BatchListFilter.tsx';
import { BatchMobileListItem } from './batch/components/BatchListMobile.tsx';
import { BatchTableDesktop } from './batch/components/BatchTableDesktop.tsx';

export interface IBatchFilters {
	status?: BatchStatus[];
	paymentType?: BATCH_OPERATION_TYPE[];
	dateTo?: string;
	dateFrom?: string;
}

export const BatchOperations = () => {
	const { t } = useTranslation('', { keyPrefix: 'listOfOperations' });
	const { hasCapability } = useBankProfile();
	const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
	const canCreatePendingPayment = hasCapability(Capability.CREATE_PAYMENT);
	const emptyStateKey = canCreatePendingPayment ? 'empty.pending' : 'empty.readOnly';
	const {
		currentPage,
		setCurrentPage,
		pageSize,
		pageOptions,
		handlePageSizeChange,
		pageText,
		totalCountText,
	} = usePagination();

	const defaultFilters = useMemo(() => getDefaultFilters(), []);

	const [filters, setFilters] = useState<IBatchFilters>(defaultFilters);
	const { data, isLoading } = useBatches({
		currentPage,
		limit: pageSize,
		...filters,
		status: filters.status?.length ? filters.status : defaultFilters.status,
	});

	const filterForm = useForm<IBatchFilters>({
		defaultValues: defaultFilters,
	});
	const { hasFilters, handleClear } = useClearFilter<IBatchFilters>(
		filterForm,
		setFilters,
	);

	const handleFiltersSubmit = (submittedFilters: IBatchFilters) => {
		setFilters(submittedFilters);
		setCurrentPage(1);
	};

	return (
		<PageContainer icon={<OperationIcon />} title={t('title')}>
			<div className="my-space-24 flex items-center gap-space-16">
				<Button
					href="/operations"
					className="flex-row-reverse"
					icon={<TransferIcon className="w-space-16" />}
					variant="secondary"
				>
					{t('toSingle')}
				</Button>
				<FormProvider {...filterForm}>
					<BatchListFilter onFiltersSubmit={handleFiltersSubmit} />
				</FormProvider>
			</div>

			{isLoading && (
				<div className="h-[500px] flex justify-center items-center">
					<Loader />
				</div>
			)}

			{!isLoading &&
				(!data?.items?.length ? (
					hasFilters ? (
						<NoFilteredResults onClear={handleClear} />
					) : (
						<EmptyState
							buttonProps={
								canCreatePendingPayment
									? {
											children: t('empty.buttonText'),
											href: '/payments/batch',
										}
									: undefined
							}
							icon={<OperationIcon />}
							subTitle={t(`${emptyStateKey}.subTitle`)}
							title={t(`${emptyStateKey}.title`)}
						/>
					)
				) : (
					<div className="mt-space-24 flex flex-col gap-space-16">
						{isMobile ? (
							data.items.map((item) => (
								<BatchMobileListItem key={item.uuid} item={item} />
							))
						) : (
							<BatchTableDesktop isLoading={isLoading} items={data.items} />
						)}
					</div>
				))}

			{data?.pagination && data?.pagination.count > pageSize && (
				<Pagination
					className="mt-space-24!"
					currentPage={currentPage}
					dropdownOpenPosition={DropdownPositions.TOP}
					onPageChange={setCurrentPage}
					onPageSizeChange={handlePageSizeChange}
					pageOptions={pageOptions}
					pageSize={pageSize}
					pageText={pageText}
					totalCount={data?.pagination.count ?? DEFAULT_PAGE_SIZE}
					totalCountText={totalCountText}
				/>
			)}
		</PageContainer>
	);
};
